import { useMemo, useState } from 'react'
import { Plus } from 'lucide-react'
import { Fab } from '@components/Fab.tsx'
import { Header } from '@components/Header.tsx'
import { useLocalStorage } from '@hooks/useLocalStorage.ts'
import { useFavicon } from '@hooks/useFavicon.ts'
import { useResource } from '@hooks/useResource.ts'
import { api } from '@api'
import { useMode } from '@mode/ModeContext.tsx'
import { ModeToggle } from '@mode/ModeToggle.tsx'
import type {
	HouseholdMember,
	Todo,
	TodoCategory,
	TodoClearCompletedBody,
	TodoTitle,
	UserId,
} from '@hewey/shared'
import { Board, type BoardColumn } from './Board.tsx'
import { PriorityColumn } from './PriorityColumn.tsx'
import { PriorityToggle } from './PriorityToggle.tsx'
import { CategoryPicker } from './CategoryPicker.tsx'
import { AddTodoDialog } from './AddTodoDialog.tsx'
import styles from './Todo.module.css'

// The fields a todo update can carry, typed off Todo rather than the request
// body so the optimistic copy can spread them in.
type Change = Partial<
	Pick<Todo, 'title' | 'completed' | 'category' | 'priority' | 'assigneeIds'>
>

export const TodoApp = () => {
	useFavicon('todo')
	const { mode } = useMode()
	// Family todos are shared, so poll to see each other's changes; personal
	// todos are single-user and don't need it.
	const todos = useResource(['todos', mode], () => api.todo.list(mode), {
		pollIntervalMs: mode === 'family' ? 10_000 : undefined,
	})
	const members = useResource(['household-members'], () =>
		api.household.members(),
	)
	const memberList = members.data?.members ?? []
	const memberMap = useMemo(
		() =>
			new Map<UserId, HouseholdMember>(memberList.map((m) => [m.id, m])),
		[memberList],
	)

	const [pickerFor, setPickerFor] = useState<Todo | null>(null)
	const [addOpen, setAddOpen] = useState(false)

	// Last-viewed lane, per mode, so the swipe position survives reloads and
	// mode switches.
	const [activeCategory, setActiveCategory] =
		useLocalStorage<TodoCategory | null>(`hewey.todo.board.col.${mode}`)

	// Priority-only view, per mode — collapses the board to a single column of
	// starred todos.
	const [priorityView = false, setPriorityView] = useLocalStorage<boolean>(
		`hewey.todo.priorityview.${mode}`,
		false,
	)

	const list = todos.data?.todos ?? []

	// A permanent General lane (category null) first, then each derived
	// category alphabetically. Within a lane, priority cards float to the top,
	// then newest-first by createdAt.
	const columns = useMemo<BoardColumn[]>(() => {
		const named = new Set<TodoCategory>()
		for (const t of list) if (t.category) named.add(t.category)
		const cats: (TodoCategory | null)[] = [
			null,
			...[...named].sort((a, b) =>
				a.toLowerCase().localeCompare(b.toLowerCase()),
			),
		]
		const sortCards = (a: Todo, b: Todo) =>
			a.priority === b.priority
				? b.createdAt.localeCompare(a.createdAt)
				: a.priority
					? -1
					: 1
		return cats.map((category) => ({
			category,
			todos: list.filter((t) => t.category === category).sort(sortCards),
		}))
	}, [list])

	const existingCategories = useMemo(
		() =>
			columns
				.map((c) => c.category)
				.filter((c): c is TodoCategory => c !== null),
		[columns],
	)

	const createTodo = async (input: {
		title: string
		category: TodoCategory | null
		priority: boolean
		assigneeIds: UserId[]
	}) => {
		try {
			const res = await api.todo.create({
				mode,
				title: input.title.slice(0, 200) as TodoTitle,
				category: input.category,
				priority: input.priority,
				assigneeIds: input.assigneeIds,
			})
			// A poll can land between the save and this response, and already
			// hold the todo.
			todos.set((prev) => ({
				todos: [
					res.todo,
					...(prev?.todos ?? []).filter((t) => t.id !== res.todo.id),
				],
			}))
		} catch {
			void todos.refetch()
		}
	}

	// A failed write needs no handling here: `mutate` refetches either way,
	// which puts the server's copy back.
	const ignore = () => {}

	const update = (todo: Todo, change: Change) =>
		todos
			.mutate(
				(prev) =>
					prev
						? {
								todos: prev.todos.map((t) =>
									t.id === todo.id ? { ...t, ...change } : t,
								),
							}
						: prev,
				() => api.todo.update(todo.id, change),
			)
			.catch(ignore)

	const onToggle = (todo: Todo, completed: boolean) =>
		update(todo, { completed })

	const onRename = (todo: Todo, title: TodoTitle) => update(todo, { title })

	const onCategoryPick = (todo: Todo, category: TodoCategory | null) => {
		setPickerFor(null)
		if (todo.category === category) return
		void update(todo, { category })
	}

	const onPriorityToggle = (todo: Todo) =>
		update(todo, { priority: !todo.priority })

	const onAssign = (todo: Todo, assigneeIds: UserId[]) =>
		update(todo, { assigneeIds })

	const clearWhere = (
		match: (t: Todo) => boolean,
		body: TodoClearCompletedBody,
	) =>
		todos
			.mutate(
				(prev) =>
					prev
						? {
								todos: prev.todos.filter(
									(t) => !(t.completed && match(t)),
								),
							}
						: prev,
				() => api.todo.clearCompleted(body),
			)
			.catch(ignore)

	const onClearCompleted = (category: TodoCategory | null) =>
		clearWhere((t) => t.category === category, { mode, category })

	const onClearCompletedPriority = () =>
		clearWhere((t) => t.priority, { mode, priority: true })

	return (
		<main className={styles.screen}>
			<Header
				left={
					<PriorityToggle
						on={priorityView}
						onChange={setPriorityView}
					/>
				}
				right={<ModeToggle />}
			/>

			{todos.error ? (
				<p className={styles.empty}>
					Couldn't load todos.{' '}
					<button
						type="button"
						className={styles.retry}
						onClick={() => void todos.refetch()}
					>
						Retry
					</button>
				</p>
			) : todos.loading && list.length === 0 ? (
				<p className={styles.empty}>Loading…</p>
			) : priorityView ? (
				<PriorityColumn
					columns={columns}
					members={memberList}
					memberMap={memberMap}
					onToggle={(todo, next) => void onToggle(todo, next)}
					onRename={(todo, title) => void onRename(todo, title)}
					onTagClick={(todo) => setPickerFor(todo)}
					onPriorityToggle={(todo) => void onPriorityToggle(todo)}
					onAssign={(todo, ids) => void onAssign(todo, ids)}
					onClearCompleted={() => void onClearCompletedPriority()}
				/>
			) : (
				<Board
					columns={columns}
					initialCategory={activeCategory}
					onActiveCategory={setActiveCategory}
					members={memberList}
					memberMap={memberMap}
					onToggle={(todo, next) => void onToggle(todo, next)}
					onRename={(todo, title) => void onRename(todo, title)}
					onTagClick={(todo) => setPickerFor(todo)}
					onPriorityToggle={(todo) => void onPriorityToggle(todo)}
					onAssign={(todo, ids) => void onAssign(todo, ids)}
					onClearCompleted={(category) =>
						void onClearCompleted(category)
					}
				/>
			)}

			<CategoryPicker
				open={pickerFor !== null}
				current={pickerFor?.category ?? null}
				existing={existingCategories}
				onPick={(category) => {
					if (pickerFor) void onCategoryPick(pickerFor, category)
				}}
				onClose={() => setPickerFor(null)}
			/>

			<AddTodoDialog
				open={addOpen}
				mode={mode}
				defaultCategory={activeCategory ?? null}
				existingCategories={existingCategories}
				members={memberList}
				onClose={() => setAddOpen(false)}
				onCreate={(input) => void createTodo(input)}
			/>

			<Fab icon={Plus} label="Add" onClick={() => setAddOpen(true)} />
		</main>
	)
}
